import { useTranslation } from 'react-i18next';
import { MouseEventHandler, ReactNode, useEffect, useMemo, useState } from 'react';
import { ContentBox } from '../../lib/components/compound/pages/contentBox';
import { ConfirmationDialog, ContentHead, Loading } from '../../lib/components';
import {
  MonthSelectionDialog,
  AssignmentMethodSelectionDialog,
  AutomaticAssignmentDialog,
  LoadingDialog
} from './customBenefitDialogs';
import { CompanyTable } from '../company/settings/shared/companyTable';
import { useNavigate } from 'react-router';
import { useStore } from '../../hooks/useStore';
import {
  DataGridProProps,
  GridActionsCellItem,
  GridEnrichedColDef,
  GridRenderCellParams,
  GridRowParams,
  useGridApiContext
} from '@mui/x-data-grid-pro';
import { Box, ChipOwnProps, Typography } from '@mui/material';
import { Chip } from '../../lib/components';
import { DeleteIcon, EditIcon, EmployeesSquareIcon, SynchronizeIcon } from '../../lib/icons';
import { AddButton } from '../company/settings/shared/addButton';
import { CustomBenefitIcon } from '../../lib/components/compound/dataDisplay/customBenefitIcon';
import { useCreateCustomBenefit } from '../actions/createCustomBenefit/hooks/useCreateCustomBenefit';
import {
  CompanyCustomBenefit,
  CustomBenefitStatusEnum,
  IconEnum
} from '@pxtra-client-lib-legacy/gen';
import { observer } from 'mobx-react-lite';
import { max } from 'lodash';
import i18n from '../../misc/i18n';

type RowAction = 'edit' | 'assignEmployees' | 'discardChanges' | 'delete';

export type CustomBenefitRow = {
  id: string;
  label?: string;
  subLabel?: string;
  benefitName: string;
  customBenefitId: string;
  status: CustomBenefitStatusEnum;
  icon: IconEnum | 'update' | 'unsaved_changes';
  actions: RowAction[];
  path: string[];
};

type ChipStyle = {
  label: string;
  variant: 'outlined' | 'filled';
  color: ChipOwnProps['color'];
};

type DeleteDialogInfo = {
  id: string;
  name: string;
  status: CustomBenefitStatusEnum;
};

export const CustomBenefits = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { benefitsStore, companyStore } = useStore();
  const { newCustomBenefit, patchCustomBenefit: patch } = useCreateCustomBenefit();

  const [deleteDialogInfo, setDeleteDialogInfo] = useState<DeleteDialogInfo>();
  const [discardDraftDialogInfo, setDiscardDraftDialogInfo] = useState<DeleteDialogInfo>();
  const [showAssignmentMonthDialog, setShowAssignmentMonthDialog] = useState(false);
  const [showAssignmentMethodDialog, setShowAssignmentMethodDialog] = useState(false);
  const [showAutomaticAssignmentDialog, setShowAutomaticAssignmentDialog] = useState(false);
  const [manualAssignmentSelected, setManualAssignmentSelected] = useState(false);
  const [rows, setRows] = useState<CustomBenefitRow[]>([]);
  const [isLoadingEmployeeData, setIsLoadingEmployeeData] = useState<boolean>(true);
  const [isValidatingConstraints, setIsValidatingConstraints] = useState<boolean>(false);

  const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1, 1));

  const chipStyle: Record<CustomBenefitStatusEnum, ChipStyle> = useMemo(
    () => ({
      draft: { label: t('receipts:STATUS_DRAFTED'), variant: 'outlined', color: 'neutral' },
      active: {
        label: t('custom:active'),
        variant: 'filled',
        color: 'success'
      }
    }),
    []
  );

  const getLocalizedName = (item: CompanyCustomBenefit) => {
    if (i18n.language === 'en' && item.name_en) {
      return item.name_en;
    }
    if (i18n.language === 'de' && item.name_de) {
      return item.name_de;
    }
    return item.name ?? 'untitled';
  };

  const handleClickCreate = () => navigate('/actions/custom-benefit/create_custom_benefit/1');

  const handleClickEdit = async (benefitId: string, step: number) => {
    const result = await companyStore.fetchCustomBenefit(benefitId);
    newCustomBenefit.loadCustomBenefit(result);
    newCustomBenefit.editingBenefitId = benefitId;
    if (step === 1) {
      navigate('/actions/custom-benefit/edit_custom_benefit/1');
    } else if (step === 3) {
      if (newCustomBenefit.companyAmount === undefined) {
        // show AssignmentMethodDialog if there is no global assignment yet
        setShowAssignmentMethodDialog(true);
      } else if (!newCustomBenefit.employeeAssignmentsStart && newCustomBenefit.companyAmountSet) {
        // if a global assignment already exists check if there is an assignment draft already and otherwise ask for the month the edited assignments are supposed to start
        setShowAssignmentMonthDialog(true);
      } else {
        // if there is an assignment draft already open the assignment table with the draft's data
        if (!newCustomBenefit.employeeAssignmentsStart)
          newCustomBenefit.employeeAssignmentsStart = max([
            newCustomBenefit.companyStart,
            nextMonth
          ]);
        navigate('edit_custom_benefit_assignment');
      }
    }
  };

  const loadSettings = () => {
    if (!companyStore.customBenefitSettings) return;

    const settings = companyStore.customBenefitSettings;

    setRows(
      settings
        .flatMap((item) => {
          const localizedBenefitName = getLocalizedName(item);

          if (item.pending_employees_count > 0) {
            // This item has unapplied assignments
            return [
              {
                id: item.id,
                customBenefitId: item.id,
                label: localizedBenefitName,
                benefitName: localizedBenefitName,
                status: item.status ?? 'draft',
                icon: item.icon,
                path: [item.id],
                actions: ['edit']
              },
              {
                id: `${item.id}-pendingEmployees`,
                customBenefitId: item.id,
                subLabel:
                  item.pending_employees_count +
                  ' ' +
                  t('custom:unsaved_assignments', { count: item.pending_employees_count }),
                benefitName: localizedBenefitName,
                status: 'draft',
                icon: 'unsaved_changes',
                path: [item.id, `${item.id}-pendingEmployees`],
                actions: ['assignEmployees', 'discardChanges']
              }
            ] as CustomBenefitRow[];
          } else if (item.status == 'draft') {
            // This item is a draft custom benefit
            return {
              id: item.id,
              customBenefitId: item.id,
              label: item.name ?? 'untitled',
              benefitName: item.name ?? 'untitled',
              status: item.status ?? 'draft',
              icon: item.icon,
              path: [item.id],
              actions: ['edit', 'delete']
            } as CustomBenefitRow;
          } else {
            // This item is an active custom benefit
            return {
              id: item.id,
              customBenefitId: item.id,
              label: item.name ?? 'untitled',
              benefitName: item.name ?? 'untitled',
              status: item.status ?? 'draft',
              icon: item.icon,
              path: [item.id],
              actions: ['edit', 'assignEmployees']
            } as CustomBenefitRow;
          }
        })
        .sort((a, b) => a.label?.localeCompare(b.label ?? '') ?? 1)
    );

    setIsLoadingEmployeeData(false);
  };

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (row) => row.path;

  useEffect(() => {
    loadSettings();
  }, [companyStore.customBenefitSettings]);

  useEffect(() => {
    setIsLoadingEmployeeData(true);
    const abortController = new AbortController();

    const fetchCustomBenefitSettings = async () => {
      try {
        await companyStore.fetchCustomBenefitSettings();
        loadSettings();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };
    fetchCustomBenefitSettings();

    return () => abortController.abort();
  }, [companyStore.company?.id]);

  const columns: Array<GridEnrichedColDef> = [
    {
      field: 'status',
      headerName: t('common:STATUS'),
      flex: 2,
      type: 'singleSelect',
      valueOptions: [
        {
          value: 'draft',
          label: t('receipts:STATUS_DRAFTED')
        },
        {
          value: 'active',
          label: t('custom:active')
        }
      ],
      renderCell: ({ value }: GridRenderCellParams<CustomBenefitStatusEnum>) => {
        return (
          value && (
            <Chip
              label={chipStyle[value].label}
              color={chipStyle[value].color}
              variant={chipStyle[value].variant}
            />
          )
        );
      }
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: (params: GridRowParams) => actions(params)
    }
  ];

  const actions = (params: GridRowParams) => {
    const actionMapping: Record<RowAction, ReactNode> = {
      assignEmployees: (
        <GridActionsCellItem
          icon={<EmployeesSquareIcon color={'primary.main'} />}
          onClick={() => {
            handleClickEdit(params.row.customBenefitId, 3);
          }}
          label={t('common:ASSIGN')}
          key={`${params.row.id}-assign`}
          showInMenu
          placeholder={''}
        />
      ),
      edit: (
        <GridActionsCellItem
          icon={<EditIcon color={'primary.main'} />}
          onClick={() => handleClickEdit(params.row.id, 1)}
          label={t('common:EDIT')}
          key={`${params.row.id}-view`}
          showInMenu
          placeholder={''}
        />
      ),
      delete: (
        <GridActionsCellItem
          icon={<DeleteIcon color={'error.main'} />}
          onClick={() => {
            setDeleteDialogInfo({
              id: params.row.id,
              name: params.row.benefitName,
              status: params.row.status
            });
            benefitsStore.updateCurrent(params.row);
          }}
          label={t('common:DELETE')}
          key={`${params.row.id}-delete`}
          showInMenu
          sx={{ color: 'error.main' }}
          placeholder={''}
        />
      ),
      discardChanges: (
        <GridActionsCellItem
          icon={<DeleteIcon color={'error.main'} />}
          onClick={() => {
            setDiscardDraftDialogInfo({
              id: params.row.path[0],
              name: params.row.benefitName,
              status: params.row.status
            });
          }}
          label={t('common:DISCARD')}
          key={`${params.row.id}-discard`}
          showInMenu
          sx={{ color: 'error.main' }}
          placeholder={''}
        />
      )
    };

    return params.row.actions.map((action: RowAction) => actionMapping[action]);
  };

  const renderTitleCell = ({
    label,
    subLabel,
    bold,
    icon,
    onClick
  }: {
    label: string;
    subLabel?: string;
    bold: boolean;
    icon: ReactNode;
    onClick: MouseEventHandler<HTMLDivElement>;
  }) => {
    return (
      <Box sx={{ display: 'flex' }} onClick={onClick}>
        {icon}
        <Typography component="span" style={{ fontWeight: bold ? '600' : '400' }}>
          {label}
        </Typography>
        {subLabel && (
          <Typography component="span" style={{ fontWeight: 'normal', marginLeft: '5px' }}>
            {subLabel}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('common:CUSTOM_BENEFITS')}
          showActions={true}
          renderActions={() => (
            <AddButton
              text={t('common:CREATE')}
              startIcon={<EditIcon />}
              onClick={handleClickCreate}
            />
          )}
        />
      }
    >
      {isLoadingEmployeeData ? (
        <Loading />
      ) : (
        <CompanyTable
          rows={rows}
          columns={columns}
          hideToolbar={rows.length === 0}
          hideHeader={rows.length === 0}
          onRowDoubleClick={(params) => {
            handleClickEdit(params.row.id, 1);
          }}
          groupingColDef={{
            headerName: t('common:BENEFIT'),
            flex: 3,
            renderCell: (params) => {
              const { id, field, rowNode } = params;
              const apiRef = useGridApiContext();

              const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
                if (!rowNode.children || rowNode.children.length == 0) {
                  return;
                }

                apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
                apiRef.current.setCellFocus(id, field);
                event.stopPropagation();
              };

              const icon =
                params.row.icon == 'update' ? (
                  <SynchronizeIcon
                    width={32}
                    height={32}
                    color="heaven.main"
                    sx={{ mr: 2, borderRadius: '8px !important' }}
                  />
                ) : params.row.icon == 'unsaved_changes' ? (
                  <EmployeesSquareIcon
                    width={32}
                    height={32}
                    color="heaven.main"
                    sx={{ mr: 2, borderRadius: '8px !important' }}
                  />
                ) : (
                  <CustomBenefitIcon
                    iconKey={params.row.icon}
                    size={32}
                    sx={{ mr: 2, borderRadius: '8px !important' }}
                  />
                );

              return renderTitleCell({
                label: params.row.label,
                subLabel: params.row.subLabel,
                bold: true,
                icon,
                onClick: handleClick
              });
            }
          }}
          getTreeDataPath={getTreeDataPath}
          defaultGroupingExpansionDepth={1}
          treeData
        />
      )}

      {deleteDialogInfo && (
        <ConfirmationDialog
          open={true}
          title={t('custom:delete_confirmation', {
            defaultValue: '',
            benefit: deleteDialogInfo.name
          })}
          contentText={t('custom:delete_confirmation_hint', {
            defaultValue: '',
            benefit: deleteDialogInfo.name
          })}
          variant={'delete'}
          onCancel={() => {
            setDeleteDialogInfo(undefined);
          }}
          onConfirm={async () => {
            await benefitsStore.delete(deleteDialogInfo);
            loadSettings();
          }}
          onClose={() => setDeleteDialogInfo(undefined)}
          confirmButtonText={t('common:DELETE')}
        />
      )}

      {discardDraftDialogInfo && (
        <ConfirmationDialog
          open={true}
          title={t('custom:discard_draft')}
          contentText={t('custom:delete_confirmation_hint')}
          variant={'delete'}
          onCancel={() => {
            setDiscardDraftDialogInfo(undefined);
          }}
          onConfirm={async () => {
            await benefitsStore.discardDraft(discardDraftDialogInfo);
            loadSettings();
          }}
          onClose={() => setDiscardDraftDialogInfo(undefined)}
          confirmButtonText={t('common:DELETE')}
        />
      )}

      {showAssignmentMonthDialog && (
        <MonthSelectionDialog
          title={t('custom:choose_assignment_month')}
          minStartDate={
            newCustomBenefit.companyStart > nextMonth ? newCustomBenefit.companyStart : nextMonth
          }
          confirmButtonText={t('common:CONFIRM')}
          cancelButtonText={t('common:CANCEL')}
          onClose={() => {
            newCustomBenefit.clear();
            setShowAssignmentMonthDialog(false);
          }}
          onCancel={() => {
            newCustomBenefit.clear();
          }}
          open={showAssignmentMonthDialog}
          onConfirm={async (date) => {
            newCustomBenefit.employeeAssignmentsStart = date
              ? new Date(date.getFullYear(), date.getMonth(), 1)
              : undefined;
            if (manualAssignmentSelected && newCustomBenefit.editingBenefitId) {
              setIsValidatingConstraints(true);
              newCustomBenefit.companyStart = date
                ? new Date(date.getFullYear(), date.getMonth(), 1)
                : new Date();
              await patch(newCustomBenefit.editingBenefitId);
              setIsValidatingConstraints(false);
            }
            setShowAssignmentMethodDialog(false);
            navigate('edit_custom_benefit_assignment');
          }}
        />
      )}
      {showAssignmentMethodDialog && (
        <AssignmentMethodSelectionDialog
          title={t('custom:choose_assignment_method')}
          confirmButtonText={t('common:CONFIRM')}
          cancelButtonText={t('common:CANCEL')}
          onClose={() => {
            newCustomBenefit.clear();
            setShowAssignmentMethodDialog(false);
          }}
          open={showAssignmentMethodDialog}
          onConfirm={(selectedMethod) => {
            if (selectedMethod === 'automatic') {
              setShowAutomaticAssignmentDialog(true);
            } else {
              setManualAssignmentSelected(true);
              newCustomBenefit.companyAmount = 0;
              setShowAssignmentMonthDialog(true);
            }
            setShowAssignmentMethodDialog(false);
          }}
          onCancel={() => {
            newCustomBenefit.clear();
          }}
        />
      )}
      {showAutomaticAssignmentDialog && (
        <AutomaticAssignmentDialog
          title={t('custom:automatic_assignment')}
          confirmButtonText={t('common:CONFIRM')}
          cancelButtonText={t('common:CANCEL')}
          onClose={() => {
            newCustomBenefit.clear();
            setShowAutomaticAssignmentDialog(false);
          }}
          open={showAutomaticAssignmentDialog}
          onConfirm={async ([companyAmount, startMonth, startYear]) => {
            newCustomBenefit.companyAmount = companyAmount;
            newCustomBenefit.companyStart = new Date(startYear, startMonth, 1);
            newCustomBenefit.employeeAssignmentsStart = new Date(startYear, startMonth, 1);
            if (newCustomBenefit.editingBenefitId) {
              setIsValidatingConstraints(true);
              await patch(newCustomBenefit.editingBenefitId);
            }
            setIsValidatingConstraints(false);
            navigate('edit_custom_benefit_assignment');
          }}
          onCancel={() => {
            newCustomBenefit.clear();
          }}
        />
      )}
      {isValidatingConstraints && (
        <LoadingDialog text={t('custom:validating_assignments')} open={isValidatingConstraints} />
      )}
    </ContentBox>
  );
});
